import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, createContext, useContext } from "react";
import LoginPage from "@/pages/LoginPage";
import DashboardLayout from "@/layouts/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import PerfumeList from "@/pages/PerfumeList";
import Sales from "@/pages/Sales";
import GlsLabels from "@/pages/GlsLabels";
import Revenue from "@/pages/Revenue";
import Analytics from "@/pages/Analytics";
import Scorporo from "@/pages/Scorporo";
import Settings from "@/pages/Settings";
import Transit from "@/pages/Transit";
import TransitOut from "@/pages/TransitOut";
import TransitIn from "@/pages/TransitIn";
import TransitHistory from "@/pages/TransitHistory";
import RealTime from "@/pages/RealTime";
import Integrations from "@/pages/Integrations";
import QuickOrder from "@/pages/QuickOrder";
import FillingRegister from "@/pages/FillingRegister";
import LivePage from "@/pages/LivePage";
import RevenueDetails from "@/pages/RevenueDetails";
import LabelPrint from "@/pages/LabelPrint";
import Customers from "@/pages/Customers";
import AdvancedAnalytics from "@/pages/AdvancedAnalytics";
import Corrispettivi from "@/pages/Corrispettivi";
import Spedizioni from "@/pages/Spedizioni";
import PickingList from "@/pages/PickingList";
import SDSGenerator from "@/pages/SDSGenerator";
import Scanner from "@/pages/Scanner";
import SplashScreen from "@/components/SplashScreen";

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = (tokenData, userData) => {
    localStorage.setItem("token", tokenData);
    localStorage.setItem("user", JSON.stringify(userData));
    setToken(tokenData);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-gold animate-pulse">Caricamento...</div>
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  const [showSplash, setShowSplash] = useState(true);

  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  return (
    <AuthProvider>
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="home" element={<QuickOrder />} />
            <Route path="riempimento" element={<FillingRegister />} />
            <Route path="transito" element={<Transit />} />
            <Route path="transito/uscita" element={<TransitOut />} />
            <Route path="transito/entrata" element={<TransitIn />} />
            <Route path="transito/storico" element={<TransitHistory />} />
            <Route path="tempo-reale" element={<RealTime />} />
            <Route path="live" element={<LivePage />} />
            <Route path="integrazioni" element={<Integrations />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="perfume-list" element={<PerfumeList />} />
            <Route path="sales" element={<Sales />} />
            <Route path="revenue" element={<Revenue />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="scorporo" element={<Scorporo />} />
            <Route path="settings" element={<Settings />} />
            <Route path="fatturato" element={<RevenueDetails />} />
            <Route path="stampa-etichette" element={<LabelPrint />} />
            <Route path="clienti" element={<Customers />} />
            <Route path="analytics-avanzate" element={<AdvancedAnalytics />} />
            <Route path="corrispettivi" element={<Corrispettivi />} />
            <Route path="spedizioni" element={<Spedizioni />} />
            <Route path="picking-list" element={<PickingList />} />
            <Route path="sds-generator" element={<SDSGenerator />} />
            <Route path="scanner" element={<Scanner />} />
            <Route path="etichette-gls" element={<GlsLabels />} />
            <Route path="archivio-vendite" element={<Sales />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#FFFFFF',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              color: '#1A1A1A',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
